import { motion } from "framer-motion";
import { Link } from "wouter";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

export default function TheScience() {
  return (
    <div className="min-h-screen bg-gradient-landing-hero font-sans">

      {/* ── Hero ── */}
      <section className="py-24 px-6 text-center">
        <motion.div {...fadeUp} className="max-w-3xl mx-auto">
          <p className="text-[11px] tracking-[4px] text-navy/40 font-sans mb-6">
            EVIDENCE-BASED · SCIENCE BACKED · PERSONALLY BIASED
          </p>
          <h1 className="text-[clamp(36px,5vw,52px)] font-normal leading-[1.1] text-eirvana-navy mb-6 -tracking-tight">
            The Science<br />
            <span className="text-eirvana-gold italic">Behind Eirvana</span>
          </h1>
          <p className="text-[17px] leading-[1.8] text-navy/55 font-sans max-w-[520px] mx-auto">
            Perimenopause and menopause are not mysteries to be endured. They are
            biological events — and biology can be understood, tracked, and navigated.
          </p>
        </motion.div>
      </section>

      {/* ── Hormonal Biology ── */}
      <section className="py-16 px-6 bg-gradient-landing-cards">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-14">
            <h2 className="text-[32px] font-normal text-eirvana-navy mb-4">
              What's Actually Happening in Your Body
            </h2>
            <p className="text-[15px] leading-[1.8] text-navy/55 font-sans max-w-[560px] mx-auto">
              The transition begins years before your last period. Understanding the hormonal
              cascade helps decode symptoms that are often dismissed as stress or aging.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                label: "Estrogen",
                color: "#C9A84C",
                body:
                  "Estradiol levels fluctuate erratically in perimenopause before declining. This affects sleep, cognition, bone density, cardiovascular health, and over 400 bodily processes.",
              },
              {
                label: "Progesterone",
                color: "#0B1F3A",
                body:
                  "Often the first hormone to decline, progesterone loss triggers anxiety, poor sleep, and irregular cycles — frequently years before estrogen drops noticeably.",
              },
              {
                label: "Cortisol & Insulin",
                color: "#4A7DAA",
                body:
                  "Hormonal shifts lower stress resilience and increase insulin resistance, explaining weight changes, brain fog, and fatigue that labs rarely flag.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-sm"
              >
                <div
                  className="w-2 h-12 rounded-full mb-6"
                  style={{ backgroundColor: item.color }}
                />
                <h3 className="text-[18px] font-normal text-eirvana-navy mb-3">{item.label}</h3>
                <p className="text-[14px] leading-[1.7] text-navy/60 font-sans">{item.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── The Diagnostic Gap ── */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <motion.div {...fadeUp}>
            <h2 className="text-[32px] font-normal text-eirvana-navy mb-6">
              Why "Everything Looks Normal" Is the Wrong Answer
            </h2>
            <p className="text-[15px] leading-[1.8] text-navy/55 font-sans mb-5">
              Standard blood panels measure a single snapshot of hormones in a system
              that fluctuates daily. A normal TSH or FSH on one day tells you almost
              nothing about your symptoms on another.
            </p>
            <p className="text-[15px] leading-[1.8] text-navy/55 font-sans mb-5">
              Eirvana tracks patterns over time — correlating symptoms, sleep, stress,
              nutrition, and cycle data — to reveal what a single lab visit cannot.
            </p>
            <p className="text-[15px] leading-[1.8] text-navy/55 font-sans">
              This is not alternative medicine. It is better use of existing science.
            </p>
          </motion.div>

          <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.15 }} className="space-y-4">
            {[
              { stat: "73%", desc: "of women with perimenopause symptoms are initially misdiagnosed" },
              { stat: "4–15", desc: "years average time to receive adequate support" },
              { stat: "34+", desc: "recognized symptoms of perimenopause — most go unconnected" },
            ].map((s, i) => (
              <div
                key={i}
                className="flex items-start gap-5 bg-white rounded-xl px-6 py-5 shadow-sm border-l-4 border-eirvana-gold"
              >
                <span className="text-[32px] text-eirvana-gold leading-none shrink-0">{s.stat}</span>
                <p className="text-[14px] leading-[1.6] text-navy/60 font-sans pt-1">{s.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── How AI Helps ── */}
      <section className="py-16 px-6 bg-gradient-landing-dot">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-14">
            <h2 className="text-[32px] font-normal text-eirvana-navy mb-4">
              Where AI Changes the Equation
            </h2>
            <p className="text-[15px] leading-[1.8] text-navy/55 font-sans max-w-[540px] mx-auto">
              The human body generates far more signal than any single consultation can
              interpret. AI is not a replacement for clinical care — it is a tool that
              finally makes the full picture visible.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: "Pattern Recognition Across Time",
                body:
                  "Eirvana identifies correlations across weeks and months of symptom data — the kind of longitudinal analysis that a 15-minute appointment structurally cannot provide.",
              },
              {
                title: "Personalization, Not Averages",
                body:
                  "Clinical guidelines are built on population averages. Your biology is not average. Our models adapt to your specific symptom fingerprint, culture, and history.",
              },
              {
                title: "Continuous Learning",
                body:
                  "As you log more data, insights sharpen. Eirvana learns what interventions help you — not what helped the median trial participant.",
              },
              {
                title: "Plain Language Explanations",
                body:
                  "Science should be accessible. Eirvana translates complex hormonal biology into clear, actionable language — so you can ask better questions and advocate for yourself.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="bg-white rounded-2xl p-8 shadow-sm"
              >
                <h3 className="text-[17px] font-normal text-eirvana-navy mb-3">{item.title}</h3>
                <p className="text-[14px] leading-[1.7] text-navy/60 font-sans">{item.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Clinical Backing ── */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <div className="w-16 h-[2px] bg-eirvana-gold mx-auto mb-10" />
            <p className="text-[20px] leading-[1.7] text-eirvana-navy italic mb-6">
              "Eirvana's approach mirrors what evidence-based menopause medicine has known
              for decades — symptoms are interconnected, individual, and deserve to be taken seriously."
            </p>
            <p className="text-[11px] tracking-[3px] text-eirvana-gold font-sans">
              DR. PURVA TRIPATHI · DNB FRCR · BRITISH MENOPAUSE SOCIETY CERTIFIED · NHS CONSULTANT
            </p>
            <div className="w-16 h-[2px] bg-eirvana-gold mx-auto mt-10" />
          </motion.div>
        </div>
      </section>

      {/* ── Our Approach ── */}
      <section className="py-16 px-6 bg-gradient-landing-cards">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-12">
            <h2 className="text-[32px] font-normal text-eirvana-navy mb-4">
              Our Clinical Principles
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                n: "01",
                title: "Evidence First",
                body:
                  "Every insight Eirvana surfaces is grounded in peer-reviewed research on hormonal health, symptoms, and intervention efficacy.",
              },
              {
                n: "02",
                title: "Complement, Don't Replace",
                body:
                  "We help you understand your body so you can have better conversations with your clinician — not bypass them.",
              },
              {
                n: "03",
                title: "Culturally Informed",
                body:
                  "Symptoms, thresholds, and care preferences differ across cultures. Eirvana is built to reflect your whole lived experience.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-sm"
              >
                <div className="text-[13px] tracking-[3px] text-eirvana-gold font-sans mb-4">{item.n}</div>
                <h3 className="text-[17px] font-normal text-eirvana-navy mb-3">{item.title}</h3>
                <p className="text-[14px] leading-[1.7] text-navy/60 font-sans">{item.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 px-6 text-center">
        <motion.div {...fadeUp} className="max-w-xl mx-auto">
          <h2 className="text-[28px] font-normal text-eirvana-navy mb-5">
            Ready to understand your body?
          </h2>
          <p className="text-[15px] leading-[1.8] text-navy/50 font-sans mb-10">
            Join founding members getting early access to Eirvana.
          </p>
          <Link
            href="/register"
            className="no-underline inline-block bg-eirvana-navy text-eirvana-gold text-[12px] px-[32px] py-[14px] rounded-full font-sans tracking-[1.5px] font-medium hover:opacity-90 transition-opacity"
          >
            GET STARTED
          </Link>
        </motion.div>
      </section>

      {/* ── Footer ── */}
      <footer className="flex justify-between items-center px-8 py-5 bg-eirvana-footer border-t border-gold/10">
        <span className="text-[10px] text-gold/35 font-sans tracking-[1px]">
          © 2025 EIRVANA · HIPAA COMPLIANT · YOUR DATA IS YOURS
        </span>
        <a
          href="mailto:founders@eirvana.one"
          className="no-underline hover:opacity-80 transition-opacity text-[10px] text-gold/35 font-sans tracking-[1px]"
        >
          founders@eirvana.one
        </a>
      </footer>
    </div>
  );
}
